import { logger } from '../services/logger';
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from './db';
import { useAuth } from './auth';

// Define types for the payloads
interface WalletPayload {
  new: {
    balance: number;
    user_id: string;
    updated_at: string;
  };
}

interface WalletContextType {
  balance: number;
  loading: boolean;
  error: string | null;
  refreshBalance: () => Promise<void>;
  updateBalance: (newBalance: number) => void;
  formatCurrency: (amount: number) => string;
}

const WalletContext = createContext<WalletContextType>({
  balance: 0,
  loading: true,
  error: null,
  refreshBalance: async () => {},
  updateBalance: () => {},
  formatCurrency: () => 'R0.00',
});

export function WalletProvider({ children }: { children: React.ReactNode }) {
  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  /** Avoid flashing R0.00 in the header while a refresh is in flight */
  const balanceBeforeRefresh = useRef<number | null>(null);

  const refreshBalance = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    balanceBeforeRefresh.current = balance;
    
    try {
      const { data, error: walletError } = await supabase
        .from('user_wallets')
        .select('balance')
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (walletError) throw walletError;
      
      // If wallet doesn't exist yet, create one with zero balance
      if (!data) {
        const { data: newWallet, error: createError } = await supabase
          .from('user_wallets')
          .insert({ user_id: user.id, balance: 0 })
          .select()
          .single();
          
        if (createError) throw createError;
        
        setBalance(0); // New wallet has 0 balance
      } else {
        setBalance(data.balance || 0);
      }
    } catch (err) {
      logger.error('Error fetching wallet balance:', err);
      setError('Failed to load wallet balance');
    } finally {
      setLoading(false);
      balanceBeforeRefresh.current = null;
    }
  };
  
  const updateBalance = (newBalance: number) => {
    if (newBalance < 0 || Number.isNaN(newBalance)) return;
    setBalance(newBalance);
  };
  
  const formatCurrency = (amount: number): string => {
    return `R${amount.toFixed(2)}`;
  };
  
  useEffect(() => {
    if (user) {
      refreshBalance();
      
      // Subscribe to wallet changes
      const walletSubscription = supabase
        .channel('wallet_updates')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_wallets',
            filter: `user_id=eq.${user.id}`
          },
          (payload: WalletPayload) => {
            if (payload.new) {
              setBalance(payload.new.balance);
            }
          }
        )
        .subscribe();
      
      // Do not set balance from wallet_transactions INSERT: can race with other rows and
      // cause brief wrong values. user_wallets updates + place-bet API wallet_balance are canonical.

      // Also listen for custom events from legacy code
      const handleWalletUpdate = (e: CustomEvent<{balance: number}>) => {
        if (e.detail && typeof e.detail.balance === 'number') {
          setBalance(e.detail.balance);
        }
      };
      
      window.addEventListener('walletBalanceUpdated', handleWalletUpdate as EventListener);
      
      return () => {
        walletSubscription.unsubscribe();
        window.removeEventListener('walletBalanceUpdated', handleWalletUpdate as EventListener);
      };
    } else {
      setBalance(0);
    }
  }, [user]);
  
  const displayBalance =
    loading && balanceBeforeRefresh.current != null && balance === 0 && (balanceBeforeRefresh.current ?? 0) > 0
      ? balanceBeforeRefresh.current
      : balance;

  return (
    <WalletContext.Provider 
      value={{ 
        balance: displayBalance, 
        loading, 
        error, 
        refreshBalance, 
        updateBalance,
        formatCurrency
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export const useWallet = () => useContext(WalletContext); 